import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Entropy } from "./ui/entropy";
import Testimonials from "./ui/twitter-testimonial-cards";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDAR_IFRAME_SRC =
  "https://api.leadconnectorhq.com/widget/booking/kduvDOLAWJmCmYuV0G6i";
const EBOOK_FORM_SRC =
  "https://api.leadconnectorhq.com/widget/form/JTjelBIlFQucJOWEqIxb?notrack=true";

const INCLUDES = [
  {
    num: "01",
    icon: "phone",
    title: "Weekly 1-on-1 Strategy Call",
    desc: "Every week we meet online. We review your progress, solve what's not working, and plan the week ahead. You're never left figuring it out alone.",
  },
  {
    num: "02",
    icon: "map",
    title: "Custom 3-Month Growth Plan",
    desc: "Built specifically for you — your style, your city, your goals. Not a template. A real strategy that accounts for where you are and where you want to be.",
  },
  {
    num: "03",
    icon: "calendar",
    title: "Full Content Plan",
    desc: "A complete content strategy for the duration of our work together. What to post, when to post it, what format — so you never stare at a blank screen again.",
  },
  {
    num: "04",
    icon: "chat",
    title: "Unlimited DM Support",
    desc: "Got a question between calls? Send a DM. Stuck on a caption? Ask. Need a second opinion before posting? I'm there. Real support, not a ticket system.",
  },
];

const TIMELINE = [
  {
    tag: "M1",
    month: "Month One — Foundation",
    title: "Building your system from scratch",
    desc: "We audit your current Instagram, define your niche, optimise your profile and build your content foundation. By the end of month one you have a clear direction and your first content live.",
  },
  {
    tag: "M2",
    month: "Month Two — Momentum",
    title: "Consistency, reach and first results",
    desc: "We double down on what's working, refine your content mix and start converting followers into leads. Your DMs become active. Discovery calls start happening.",
  },
  {
    tag: "M3",
    month: "Month Three — Scale",
    title: "A system that runs without you watching it",
    desc: "By now your content is converting. We focus on optimising your funnel, building recurring client relationships and making sure the system keeps working after we're done.",
  },
];

const FAQS = [
  {
    q: "How many spots are available per month?",
    a: "I keep the group small on purpose — maximum 5 clients at a time. This ensures every client gets real attention, not a template.",
  },
  {
    q: "Do I need a big following to start?",
    a: "No. Most of my clients start with under 500 followers. Followers are not the goal — clients are. We build systems that convert regardless of your current audience size.",
  },
  {
    q: "How much time do I need per week?",
    a: "About 1-2 hours per week outside of our call. Most of that is content creation, which we plan together so it fits around your tattooing schedule.",
  },
  {
    q: "What if I'm not getting results?",
    a: "We adjust. Every week we review what's working and what isn't. The strategy evolves with your results. You're not locked into a plan that isn't working.",
  },
  {
    q: "Is the coaching in English?",
    a: "Yes — all coaching sessions and support are conducted in English, making this accessible to tattoo artists worldwide.",
  },
];

const Icon = ({ name }) => {
  // Minimal inline SVG icons (lucide-style)
  const common = { width: 28, height: 28, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };
  if (name === "phone")
    return (
      <svg {...common}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
      </svg>
    );
  if (name === "map")
    return (
      <svg {...common}>
        <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21 3 6" />
        <line x1="9" y1="3" x2="9" y2="18" />
        <line x1="15" y1="6" x2="15" y2="21" />
      </svg>
    );
  if (name === "calendar")
    return (
      <svg {...common}>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  if (name === "chat")
    return (
      <svg {...common}>
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
    );
  return null;
};

export default function Inkflow() {
  const navRef = useRef(null);
  const pageRef = useRef(null);
  const curRef = useRef(null);
  const curRRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingLoaded, setBookingLoaded] = useState(false);
  const [bookingKey, setBookingKey] = useState(0);
  const [ebookOpen, setEbookOpen] = useState(false);
  const [ebookLoaded, setEbookLoaded] = useState(false);
  const [ebookKey, setEbookKey] = useState(0);
  const [spots, setSpots] = useState({ filled: 3, total: 5, month: "this month", remaining: 2 });

  // Fetch live spots availability
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/spots/status`)
      .then((res) => {
        if (!cancelled && res?.data) setSpots(res.data);
      })
      .catch(() => {
        /* keep defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cursor + nav scroll + reveal observer
  useEffect(() => {
    const cur = curRef.current;
    const curR = curRRef.current;
    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    let mx = 0, my = 0, rx = 0, ry = 0, raf;
    let cursorActive = !isTouch;

    const onMove = (e) => {
      if (!cursorActive) return;
      mx = e.clientX;
      my = e.clientY;
      if (cur) {
        cur.style.left = mx + "px";
        cur.style.top = my + "px";
      }
    };
    const loop = () => {
      if (cursorActive) {
        rx += (mx - rx) * 0.1;
        ry += (my - ry) * 0.1;
        if (curR) {
          curR.style.left = rx + "px";
          curR.style.top = ry + "px";
        }
      }
      raf = requestAnimationFrame(loop);
    };
    const onOver = (e) => {
      if (!cursorActive || !pageRef.current) return;
      if (e.target.closest("a, button, .ink-faq-q")) {
        pageRef.current.classList.add("cur-hover");
      }
    };
    const onOut = (e) => {
      if (!cursorActive || !pageRef.current) return;
      if (e.target.closest("a, button, .ink-faq-q")) {
        pageRef.current.classList.remove("cur-hover");
      }
    };
    const onScroll = () => {
      if (navRef.current) {
        navRef.current.classList.toggle("s", window.scrollY > 40);
      }
    };
    const onVisibility = () => {
      // Pause cursor loop when tab is hidden to save CPU
      cursorActive = !document.hidden && !isTouch;
    };

    if (!isTouch) {
      document.addEventListener("mousemove", onMove, { passive: true });
      document.addEventListener("mouseover", onOver, { passive: true });
      document.addEventListener("mouseout", onOut, { passive: true });
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    loop();

    const ro = new IntersectionObserver(
      (entries, observer) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("v");
            observer.unobserve(e.target); // stop watching once revealed
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".ink-r").forEach((el) => ro.observe(el));

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Lock body scroll when modal is open + inject GHL form_embed.js for auto-resize
  useEffect(() => {
    const anyOpen = bookingOpen || ebookOpen;
    document.body.style.overflow = anyOpen ? "hidden" : "";
    if (anyOpen) {
      const existing = document.getElementById("ghl-form-embed");
      if (!existing) {
        const s = document.createElement("script");
        s.id = "ghl-form-embed";
        s.src = "https://link.msgsndr.com/js/form_embed.js";
        s.async = true;
        document.body.appendChild(s);
      }
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [bookingOpen, ebookOpen]);

  // Fallback: forcibly hide loader after 3s in case iframe onLoad doesn't fire (cached/reopened iframe)
  useEffect(() => {
    if (!bookingOpen) return undefined;
    const t = setTimeout(() => setBookingLoaded(true), 3000);
    return () => clearTimeout(t);
  }, [bookingOpen]);

  useEffect(() => {
    if (!ebookOpen) return undefined;
    const t = setTimeout(() => setEbookLoaded(true), 3000);
    return () => clearTimeout(t);
  }, [ebookOpen]);

  const openBooking = (e) => {
    if (e) e.preventDefault();
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "Lead", { content_name: "Apply for coaching" });
    }
    setBookingLoaded(false);
    setBookingKey((k) => k + 1);
    setBookingOpen(true);
  };

  const openEbook = (e) => {
    if (e) e.preventDefault();
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", "Lead", { content_name: "Free ebook" });
    }
    setEbookLoaded(false);
    setEbookKey((k) => k + 1);
    setEbookOpen(true);
  };

  const marqueeItems = [
    "Weekly Strategy Calls",
    "Custom Growth Plan",
    "Personal DM Support",
    "Content Strategy",
    "3 Months",
    "No Paid Ads",
    "Tattoo Niche Only",
  ];

  return (
    <div className="inkflow-page" ref={pageRef} data-testid="inkflow-page">
      <div className="ink-cur" ref={curRef} />
      <div className="ink-cur-r" ref={curRRef} />

      {/* NAV */}
      <nav className="ink-nav" ref={navRef} data-testid="main-nav">
        <a
          href="https://www.inkflowleads.com"
          className="ink-logo"
          data-testid="logo-link"
        >
          INK<span>FLOW</span>
        </a>
        <button
          type="button"
          className="ink-nav-cta"
          onClick={openBooking}
          data-testid="nav-apply-btn"
        >
          Apply now
        </button>
      </nav>

      {/* HERO */}
      <section className="ink-hero" data-testid="hero-section">
        <div className="ink-hero-left">
          <div className="ink-eyebrow">
            <div className="ink-eyebrow-line"></div>
            <span>3-Month Coaching Program</span>
          </div>
          <h1 className="ink-h1">
            GROW YOUR<br />
            <span className="g">TATTOO</span><br />
            <span className="out">BUSINESS</span>
          </h1>
          <p className="ink-hero-italic">
            From inconsistent clients to a full calendar.
          </p>
          <p className="ink-hero-desc">
            A hands-on 3-month coaching program for tattoo artists who are done
            relying on word-of-mouth and ready to build a system that brings
            clients in consistently — without paid ads.
          </p>
          <div className="ink-hero-actions">
            <button
              type="button"
              className="btn-gold"
              onClick={openBooking}
              data-testid="hero-apply-btn"
            >
              <span>Apply for coaching →</span>
            </button>
            <a href="#includes" className="btn-ghost2" data-testid="hero-see-included-link">
              See what's included
            </a>
          </div>
        </div>
        <div className="ink-hero-right" aria-hidden="true">
          <div className="ink-hero-right-inner">
            <div className="ink-entropy-wrap">
              <Entropy size={460} className="ink-entropy-desktop" />
              <Entropy size={300} className="ink-entropy-mobile" />
            </div>
          </div>
          <div className="ink-hero-ornament"></div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="ink-mq" data-testid="marquee">
        <div className="ink-mq-track">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <div className="ink-mq-item" key={i}>
              <span>{item}</span>
              <span className="ink-mq-dot"></span>
            </div>
          ))}
        </div>
      </div>

      {/* WHAT'S INCLUDED */}
      <section className="ink-section" id="includes" data-testid="includes-section">
        <div className="ink-sec-tag ink-r">
          <div className="ink-sec-tag-line"></div>
          <span>What's included</span>
        </div>
        <h2 className="ink-sec-title ink-r d1">
          EVERYTHING<br />YOU <span className="g">NEED</span>
        </h2>
        <div className="ink-includes ink-r d2">
          {INCLUDES.map((it) => (
            <div className="ink-inc-card" key={it.num} data-testid={`include-card-${it.num}`}>
              <div className="ink-inc-num">{it.num}</div>
              <div className="ink-inc-icon"><Icon name={it.icon} /></div>
              <div className="ink-inc-title">{it.title}</div>
              <p className="ink-inc-desc">{it.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="ink-section ink-timeline-section" data-testid="timeline-section">
        <div className="ink-sec-tag ink-r">
          <div className="ink-sec-tag-line"></div>
          <span>The journey</span>
        </div>
        <h2 className="ink-sec-title ink-r d1">
          3 MONTHS.<br /><span className="g">ONE</span> <span className="out">SYSTEM.</span>
        </h2>
        <div className="ink-timeline">
          {TIMELINE.map((t, i) => (
            <div className={`ink-tl-item ink-r ${i ? `d${i}` : ""}`} key={t.tag}>
              <div className="ink-tl-dot">
                <div className="ink-tl-dot-inner">{t.tag}</div>
              </div>
              <div className="ink-tl-content">
                <div className="ink-tl-month">{t.month}</div>
                <div className="ink-tl-title">{t.title}</div>
                <p className="ink-tl-desc">{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOR WHO */}
      <section className="ink-section" data-testid="forwho-section">
        <div className="ink-forwho-grid">
          <div>
            <div className="ink-sec-tag ink-r">
              <div className="ink-sec-tag-line"></div>
              <span>Is this for you?</span>
            </div>
            <h2 className="ink-sec-title ink-r d1">
              THIS IS<br /><span className="g">FOR</span><br /><span className="out">YOU IF</span>
            </h2>
            <ul className="ink-check-list ink-r d2">
              <li className="ink-check-item"><span className="ink-check-icon">✦</span> You're already tattooing but clients come and go unpredictably</li>
              <li className="ink-check-item"><span className="ink-check-icon">✦</span> You have Instagram but it's not bringing you clients</li>
              <li className="ink-check-item"><span className="ink-check-icon">✦</span> You're ready to invest in building something that lasts</li>
              <li className="ink-check-item"><span className="ink-check-icon">✦</span> You want a coach who actually understands the tattoo world</li>
              <li className="ink-check-item"><span className="ink-check-icon">✦</span> You're done with word-of-mouth being your only strategy</li>
            </ul>
            <h2 className="ink-sec-title ink-r d1" style={{ marginTop: 48, fontSize: "clamp(32px,4vw,52px)" }}>
              NOT FOR<br /><span className="out">YOU IF</span>
            </h2>
            <ul className="ink-check-list ink-r d2">
              <li className="ink-check-item"><span className="ink-cross-icon">✗</span> You're looking for a quick fix or overnight results</li>
              <li className="ink-check-item"><span className="ink-cross-icon">✗</span> You're not willing to show up consistently</li>
              <li className="ink-check-item"><span className="ink-cross-icon">✗</span> You don't have an active tattoo practice yet</li>
            </ul>
          </div>
          <div className="ink-forwho-right ink-r d2">
            <div className="ink-testimonials-wrap" data-testid="testimonials-wrap">
              <Testimonials />
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT ME */}
      <section className="ink-section ink-about-section" data-testid="about-section">
        <div className="ink-about-grid">
          <div className="ink-about-photo-wrap ink-r">
            <div className="ink-about-photo-frame">
              <img
                src="/img/about-jelle.jpg?v=2"
                alt="Inkflow founder"
                className="ink-about-photo"
                data-testid="about-photo"
              />
              <div className="ink-about-photo-corner"></div>
            </div>
          </div>
          <div className="ink-about-content">
            <div className="ink-sec-tag ink-r">
              <div className="ink-sec-tag-line"></div>
              <span>About me</span>
            </div>
            <h2 className="ink-sec-title ink-r d1">
              I LIVE<br /><span className="g">THIS</span> <span className="out">WORLD.</span>
            </h2>
            <p className="ink-about-lead ink-r d2">
              I'm not just a marketer who read about tattooing. I'm learning to
              tattoo myself — so I understand your craft from the inside.
            </p>
            <p className="ink-about-body ink-r d2">
              My coaching isn't theoretical. It's built from real understanding
              of what you do every day — the studio rhythm, the client conversations,
              the sketches at 1am, the doubt before posting. I help tattoo artists
              build a system that brings clients in consistently, without paid ads,
              without burnout, and without losing what makes their work theirs.
            </p>
            <div className="ink-about-stats ink-r d3">
              <div className="ink-about-stat">
                <div className="ink-about-stat-num">5</div>
                <div className="ink-about-stat-label">Artists per cohort</div>
              </div>
              <div className="ink-about-stat">
                <div className="ink-about-stat-num">3</div>
                <div className="ink-about-stat-label">Months together</div>
              </div>
              <div className="ink-about-stat">
                <div className="ink-about-stat-num">1:1</div>
                <div className="ink-about-stat-label">Weekly calls</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="ink-section ink-results-section" data-testid="results-section">
        <div className="ink-sec-tag ink-r" style={{ justifyContent: "center" }}>
          <div className="ink-sec-tag-line"></div>
          <span>Real Results</span>
          <div className="ink-sec-tag-line"></div>
        </div>
        <h2 className="ink-sec-title ink-r d1" style={{ textAlign: "center" }}>
          THE NUMBERS<br /><span className="g">DON'T</span> <span className="out">LIE.</span>
        </h2>

        {/* Aggregate stats strip */}
        <div className="ink-results-stats ink-r d2">
          <div className="ink-results-stat">
            <div className="ink-results-stat-num">431<span>+</span></div>
            <div className="ink-results-stat-label">Leads Generated</div>
          </div>
          <div className="ink-results-divider" />
          <div className="ink-results-stat">
            <div className="ink-results-stat-num">€0,74</div>
            <div className="ink-results-stat-label">Lowest Cost / Lead</div>
          </div>
          <div className="ink-results-divider" />
          <div className="ink-results-stat">
            <div className="ink-results-stat-num">137K<span>+</span></div>
            <div className="ink-results-stat-label">Impressions</div>
          </div>
          <div className="ink-results-divider" />
          <div className="ink-results-stat">
            <div className="ink-results-stat-num">41K<span>+</span></div>
            <div className="ink-results-stat-label">People Reached</div>
          </div>
        </div>

        {/* Glass cards with screenshots */}
        <div className="ink-results-grid ink-r d3">
          <div className="ink-result-card">
            <div className="ink-result-card-glow" />
            <div className="ink-result-card-header">
              <div className="ink-result-card-status">
                <span className="ink-result-dot"><span></span></span>
                CASE 01
              </div>
              <div className="ink-result-card-meta">100 leads · from €0,74</div>
            </div>
            <div className="ink-result-img-wrap">
              <img
                src="/img/result-1.jpg?v=2"
                alt="Campaign results case 1"
              />
            </div>
            <div className="ink-result-card-corner" />
          </div>

          <div className="ink-result-card">
            <div className="ink-result-card-glow" />
            <div className="ink-result-card-header">
              <div className="ink-result-card-status">
                <span className="ink-result-dot"><span></span></span>
                CASE 02
              </div>
              <div className="ink-result-card-meta">135 leads · €3,16 each</div>
            </div>
            <div className="ink-result-img-wrap">
              <img
                src="/img/result-2.jpg?v=2"
                alt="Campaign results case 2"
              />
            </div>
            <div className="ink-result-card-corner" />
          </div>

          <div className="ink-result-card">
            <div className="ink-result-card-glow" />
            <div className="ink-result-card-header">
              <div className="ink-result-card-status">
                <span className="ink-result-dot"><span></span></span>
                CASE 03
              </div>
              <div className="ink-result-card-meta">196 leads · €2,45 each</div>
            </div>
            <div className="ink-result-img-wrap">
              <img
                src="/img/result-3.jpg?v=2"
                alt="Campaign results case 3"
              />
            </div>
            <div className="ink-result-card-corner" />
          </div>
        </div>

        <p className="ink-results-note ink-r d4">
          Screenshots from active campaigns. Booked months in advance — fully booked artists, real conversations, real numbers.
        </p>
      </section>

      {/* PRICE */}
      <section className="ink-section ink-price-section" data-testid="price-section">
        <div className="ink-price-inner">
          <div className="ink-sec-tag ink-r" style={{ justifyContent: "center" }}>
            <div className="ink-sec-tag-line"></div>
            <span>Investment</span>
            <div className="ink-sec-tag-line"></div>
          </div>
          <h2 className="ink-sec-title ink-r d1" style={{ textAlign: "center" }}>
            SIMPLE.<br /><span className="g">TRANSPARENT.</span><br /><span className="out">FAIR.</span>
          </h2>
          <div className="ink-price-card ink-r d2">
            <div className="ink-price-card-corner"></div>
            <div className="ink-spots-live" data-testid="price-spots-live">
              <span className="ink-spots-dot"></span>
              <span className="ink-spots-text">
                <strong>{spots.filled} / {spots.total}</strong> spots filled for {spots.month} — <em>{spots.remaining} {spots.remaining === 1 ? "spot" : "spots"} left</em>
              </span>
            </div>
            <div className="ink-price-label">3-Month Coaching Program</div>
            <div className="ink-price-amount">€<span>800</span></div>
            <div className="ink-price-period">per month — 3 month commitment</div>
            <div className="ink-price-includes">
              <div className="ink-pi"><span className="ink-pi-dot"></span>Weekly 1-on-1 strategy call</div>
              <div className="ink-pi"><span className="ink-pi-dot"></span>Custom 3-month growth plan</div>
              <div className="ink-pi"><span className="ink-pi-dot"></span>Full content plan for the duration</div>
              <div className="ink-pi"><span className="ink-pi-dot"></span>Unlimited DM support</div>
              <div className="ink-pi"><span className="ink-pi-dot"></span>No hidden costs. No extra charges.</div>
            </div>
            <button
              type="button"
              className="btn-gold"
              onClick={openBooking}
              data-testid="price-apply-btn"
            >
              <span>Apply for coaching →</span>
            </button>
            <p className="ink-price-note">
              Limited spots available each month. Application required.
            </p>
          </div>

          {/* GUARANTEE */}
          <div className="ink-guarantee ink-r d3" data-testid="guarantee">
            <div className="ink-guarantee-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l8 4v6c0 5-3.5 9.5-8 10-4.5-.5-8-5-8-10V6l8-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <div className="ink-guarantee-body">
              <div className="ink-guarantee-tag">Risk-free guarantee</div>
              <h3 className="ink-guarantee-title">Don't see value in month one? Get your money back.</h3>
              <p className="ink-guarantee-text">
                I'm so confident in the system that if you complete the first month
                with full effort and don't see any value, I'll refund your first
                payment in full — no awkward emails, no fine print. The only risk
                is staying where you are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ink-section" data-testid="faq-section">
        <div className="ink-faq-inner">
          <div className="ink-sec-tag ink-r">
            <div className="ink-sec-tag-line"></div>
            <span>Questions</span>
          </div>
          <h2 className="ink-sec-title ink-r d1">
            COMMON<br /><span className="g">QUESTIONS</span>
          </h2>
          <div className="ink-faq-list ink-r d2">
            {FAQS.map((f, i) => (
              <div
                className={`ink-faq-item ${openFaq === i ? "open" : ""}`}
                key={i}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  className="ink-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`faq-question-${i}`}
                >
                  {f.q}
                  <span className="ink-faq-icon">+</span>
                </button>
                <div className="ink-faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ink-final-cta" data-testid="final-cta">
        <div className="ink-final-inner">
          <div className="ink-spots ink-r" data-testid="final-spots-live">
            <span className="ink-spots-dot"></span>
            {spots.remaining > 0
              ? `Only ${spots.remaining} of ${spots.total} spots left for ${spots.month}`
              : `Fully booked for ${spots.month} — join the waitlist for next month`}
          </div>
          <h2 className="ink-final-title ink-r d1">
            READY TO<br /><span className="g">FILL</span><br /><span className="out">YOUR CALENDAR?</span>
          </h2>
          <p className="ink-final-sub ink-r d2">
            Apply today. First call within 48 hours.
          </p>
          <div className="ink-r d3">
            <button
              type="button"
              className="btn-gold"
              onClick={openBooking}
              data-testid="final-apply-btn"
            >
              <span>Apply for coaching →</span>
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="ink-footer" data-testid="footer">
        <div className="ink-footer-top">
          <div className="ink-footer-col">
            <div className="ink-f-logo">INK<span>FLOW</span></div>
            <p className="ink-footer-tag">
              Coaching for tattoo artists who want a calendar that fills itself.
            </p>
            <div className="ink-footer-company">
              <div><strong>Inkflow / Agency Escape</strong></div>
              <div>Bergekouter 132, 9300 Aalst, België</div>
              <div>BTW: BE1005105981</div>
            </div>
          </div>

          <div className="ink-footer-col">
            <div className="ink-footer-heading">Quick links</div>
            <ul className="ink-footer-list">
              <li>
                <button type="button" onClick={openBooking} className="ink-f-link-btn" data-testid="footer-book-btn">
                  Book a call
                </button>
              </li>
              <li>
                <button type="button" onClick={openEbook} className="ink-f-link-btn" data-testid="footer-ebook-btn">
                  Free ebook
                </button>
              </li>
              <li>
                <a href="#includes" className="ink-f-link-btn">What's included</a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/jelle.agencyescape?igsh=MXB0aHV2ZG83eTd5ag%3D%3D&utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ink-f-link-btn"
                  data-testid="footer-instagram-link"
                >
                  Instagram
                </a>
              </li>
            </ul>
          </div>

          <div className="ink-footer-col">
            <div className="ink-footer-heading">Legal</div>
            <ul className="ink-footer-list">
              <li>
                <a href="/privacy" className="ink-f-link-btn" data-testid="footer-privacy-link">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="ink-f-link-btn" data-testid="footer-terms-link">
                  Algemene Voorwaarden
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="ink-footer-bottom">
          <div className="ink-f-copy">© 2026 Inkflow — Coaching for tattoo artists</div>
          <div className="ink-f-copy">Made in Belgium ✦</div>
        </div>
      </footer>

      {/* BOOKING MODAL */}
      {bookingOpen && (
        <div
          className="ink-modal-overlay"
          onClick={() => setBookingOpen(false)}
          data-testid="booking-modal"
        >
          <div
            className="ink-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="ink-modal-header">
              <div className="ink-modal-title">
                BOOK A <span>CALL</span>
              </div>
              <button
                type="button"
                className="ink-modal-close"
                onClick={() => setBookingOpen(false)}
                aria-label="Close"
                data-testid="modal-close-btn"
              >
                ×
              </button>
            </div>
            <div className="ink-modal-body">
              {!bookingLoaded && (
                <div className="ink-iframe-loader" data-testid="iframe-loader">
                  <div className="ink-iframe-spinner" />
                  <p>Loading calendar…</p>
                </div>
              )}
              <iframe
                key={`booking-${bookingKey}`}
                src={`${CALENDAR_IFRAME_SRC}${CALENDAR_IFRAME_SRC.includes("?") ? "&" : "?"}_t=${bookingKey}`}
                title="Book a coaching call"
                id={`booking-iframe-${bookingKey}`}
                onLoad={() => setBookingLoaded(true)}
                style={{
                  width: "100%",
                  height: "1400px",
                  border: "none",
                  display: "block",
                  opacity: bookingLoaded ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* EBOOK MODAL */}
      {ebookOpen && (
        <div
          className="ink-modal-overlay"
          onClick={() => setEbookOpen(false)}
          data-testid="ebook-modal"
        >
          <div
            className="ink-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="ink-modal-header">
              <div className="ink-modal-title">
                FREE <span>EBOOK</span>
              </div>
              <button
                type="button"
                className="ink-modal-close"
                onClick={() => setEbookOpen(false)}
                aria-label="Close"
                data-testid="ebook-close-btn"
              >
                ×
              </button>
            </div>
            <div className="ink-modal-body">
              {!ebookLoaded && (
                <div className="ink-iframe-loader" data-testid="ebook-iframe-loader">
                  <div className="ink-iframe-spinner" />
                  <p>Loading form…</p>
                </div>
              )}
              <iframe
                key={`ebook-${ebookKey}`}
                src={`${EBOOK_FORM_SRC}${EBOOK_FORM_SRC.includes("?") ? "&" : "?"}_t=${ebookKey}`}
                title="Get the free ebook"
                id={`ebook-iframe-${ebookKey}`}
                onLoad={() => setEbookLoaded(true)}
                style={{
                  width: "100%",
                  height: "900px",
                  border: "none",
                  display: "block",
                  opacity: ebookLoaded ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
